import { useCallback, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization/hooks";
import { getAgeVerificationMethods, getAgeVerificationStatus, postAgeVerificationFinalize, postAgeVerificationInitialize, } from "./ageVerificationSlice";
import { AgeVerificationFlow } from "./types";
const ageVerificationLogger = appLogger.withTag("AgeVerification");
export default function useAgeVerification() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [ageVerificationStatusData, setAgeVerificationStatusData] = useState(null);
    const [ageVerificationFinalizeData, setAgeVerificationFinalizeData] = useState(null);
    const [ageVerificationInitializeData, setAgeVerificationInitializeData] = useState(null);
    const [ageVerificationMethodsData, setAgeVerificationMethodsData] = useState(null);
    const { loading, loadingType } = useAppSelector((state) => state.ageVerification);
    const [ageVerificationError, setAgeVerificationError] = useState(null);
    const resetError = () => {
        setAgeVerificationError(null);
    };
    const onUserGetAgeVerificationStatusData = useCallback(async () => {
        try {
            setAgeVerificationError(null);
            const result = await dispatch(getAgeVerificationStatus()).unwrap();
            setAgeVerificationStatusData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message;
            setAgeVerificationError({
                errorType: AgeVerificationFlow.GET_AGE_VERIFICATION_STATUS,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            ageVerificationLogger.error(`Error fetching age verification status: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage };
        }
    }, [dispatch]);
    const onUserGetAgeVerificationMethods = useCallback(async (params) => {
        try {
            setAgeVerificationError(null);
            const result = await dispatch(getAgeVerificationMethods(params)).unwrap();
            setAgeVerificationMethodsData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            setAgeVerificationError({
                errorType: AgeVerificationFlow.GET_AGE_VERIFICATION_METHODS,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            ageVerificationLogger.error(`Error fetching age verification methods: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserPostAgeVerificationInitialize = useCallback(async (params, redirectScheme) => {
        try {
            setAgeVerificationError(null);
            const result = await dispatch(postAgeVerificationInitialize({
                ...params,
                redirectScheme,
            })).unwrap();
            setAgeVerificationInitializeData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            setAgeVerificationError({
                errorType: AgeVerificationFlow.POST_AGE_VERIFICATION_INITIALIZE,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            ageVerificationLogger.error(`Error initializing age verification: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const onUserPostAgeVerificationFinalize = useCallback(async (params) => {
        try {
            setAgeVerificationError(null);
            const result = await dispatch(postAgeVerificationFinalize(params)).unwrap();
            setAgeVerificationFinalizeData(result);
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError.message || t("error:fallbackError");
            setAgeVerificationError({
                errorType: AgeVerificationFlow.POST_AGE_VERIFICATION_FINALIZE,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            ageVerificationLogger.error(`Error finalizing age verification: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    return {
        ageVerificationStatusData,
        ageVerificationFinalizeData,
        ageVerificationInitializeData,
        ageVerificationMethodsData,
        isLoading: loading,
        loadingType,
        errorType: ageVerificationError?.errorType || null,
        errorMessage: ageVerificationError?.errorMessage || null,
        errorCode: ageVerificationError?.errorCode || null,
        onUserGetAgeVerificationStatusData,
        onUserGetAgeVerificationMethods,
        onUserPostAgeVerificationInitialize,
        onUserPostAgeVerificationFinalize,
        resetError,
    };
}
